import { FolderKanban, Check, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";

export default function ProjectSelector() {
  const settings = useSettings() || {};
  const { user } = useAuth();
  const projects = Array.isArray(settings.projects) ? settings.projects : [];
  const { activeProjectId, activeProject, setActiveProject } = settings;
  // v1.8.1 — strict isolation: only admins can opt into the cross-project
  // "Tous les projets" view. Commercials are pinned to a single project at
  // a time; the option simply isn't rendered for them.
  const isAdmin = user?.role === "admin";

  const label = activeProjectId
    ? activeProject?.nom || "Projet sélectionné"
    : isAdmin
      ? "Tous les projets"
      : "Sélectionner un projet";

  const duration = activeProject?.duree_jours;

  // Switch the active project then hard-refresh the page so every view
  // (dashboard, leads, inbox, reports…) re-fetches scoped to the new project.
  // We await setActiveProject first to guarantee the global default + the
  // per-user `current_projet` cookie are persisted before the reload — the
  // backend reads the cookie to scope queries, so reloading too early would
  // re-render with the old scope.
  const handleSelect = async (id) => {
    // No-op if the user re-picks the project already active — avoids a
    // pointless full-page reload.
    if ((id || null) === (activeProjectId || null)) return;
    await setActiveProject(id);
    window.location.reload();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="project-selector"
          className="group inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-card px-2 py-1 text-xs font-medium transition-colors hover:border-foreground/20"
        >
          <FolderKanban className="h-3 w-3 text-[hsl(var(--accent-gold))]" />
          <span className="truncate max-w-[180px]">{label}</span>
          {duration && (
            <span className="text-[10px] text-muted-foreground">· Programme {duration}j</span>
          )}
          <ChevronDown className="h-3 w-3 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[260px]">
        {/* "Tous les projets" — admin only (v1.8.1). Commercials must
            always be in a single project context for strict isolation. */}
        {isAdmin && (
          <>
            <DropdownMenuItem
              onClick={() => handleSelect(null)}
              data-testid="project-option-all"
              className="flex items-center justify-between"
            >
              <span>Tous les projets</span>
              {!activeProjectId && <Check className="h-3.5 w-3.5" />}
            </DropdownMenuItem>
            {projects.length > 0 && <DropdownMenuSeparator />}
          </>
        )}
        {projects.map((p) => (
          <DropdownMenuItem
            key={p.id}
            onClick={() => handleSelect(p.id)}
            data-testid={`project-option-${p.id}`}
            className="flex items-center justify-between"
          >
            <div className="min-w-0">
              <div className="truncate font-medium">{p.nom}</div>
              <div className="truncate text-[10px] text-muted-foreground">
                {p.duree_jours} jours
                {p.description && ` · ${p.description}`}
              </div>
            </div>
            {activeProjectId === p.id && <Check className="h-3.5 w-3.5 flex-none" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
